import { ref, computed } from 'vue';
import { pushToDataLayer } from './gtm';

// Chat 状态类型
export type ChatStep = 
  | 'closed'
  | 'greeting'
  | 'choose_service'
  | 'acknowledge'
  | 'choose_contact_way'
  | 'input_contact'
  | 'optional_message'
  | 'thank_you';

// 服务类型
export type ServiceType = 'sourcing' | 'maintenance' | 'custom' | 'exploring';

// 联系方式类型
export type ContactMethod = 'email' | 'whatsapp/phone' | 'wechat';

// Lead 数据
export interface ChatLeadData {
  service: ServiceType | null;
  contactMethod: ContactMethod | null;
  contactValue: string;
  message: string;
}

// 消息类型
export interface ChatMessage {
  id: number;
  type: 'system' | 'user' | 'options';
  content: string;
  options?: Array<{
    key: string;
    label: string;
    icon: string;
  }>;
}

// 全局状态（单例模式）
const isOpen = ref(false);
const currentStep = ref<ChatStep>('closed');
const messages = ref<ChatMessage[]>([]);
const leadData = ref<ChatLeadData>({
  service: null,
  contactMethod: null,
  contactValue: '',
  message: '',
});
const isSubmitting = ref(false);
const messageIdCounter = ref(0);

// 重置状态
const resetChat = () => {
  currentStep.value = 'closed';
  messages.value = [];
  leadData.value = {
    service: null,
    contactMethod: null,
    contactValue: '',
    message: '',
  };
  messageIdCounter.value = 0;
};

// 打开聊天
const openChat = (source?: string) => {
  isOpen.value = true;
  
  pushToDataLayer('chat_opened', {
    source: source || 'unknown',
  });

  // 如果是新对话，开始欢迎流程
  if (currentStep.value === 'closed' || messages.value.length === 0) {
    resetChat();
    currentStep.value = 'greeting';
  }
};

// 关闭聊天
const closeChat = () => {
  isOpen.value = false;
  
  pushToDataLayer('chat_closed', {
    step: currentStep.value,
  });
};

// 最小化聊天（保留状态）
const minimizeChat = () => {
  isOpen.value = false;
};

// 添加消息
const addMessage = (type: ChatMessage['type'], content: string, options?: ChatMessage['options']) => {
  messages.value.push({
    id: ++messageIdCounter.value,
    type,
    content,
    options,
  });
};

// 选择服务
const selectService = (service: ServiceType) => {
  leadData.value.service = service;
  currentStep.value = 'acknowledge';
  
  pushToDataLayer('chat_service_selected', {
    service,
  });
};

// 选择联系方式
const selectContactMethod = (method: ContactMethod) => {
  leadData.value.contactMethod = method;
  currentStep.value = 'input_contact';
  
  pushToDataLayer('chat_contact_method_selected', {
    method,
  });
};

// 提交联系方式
const submitContactValue = (value: string) => {
  leadData.value.contactValue = value;
  currentStep.value = 'optional_message';
};

// 提交留言
const submitMessage = (message: string) => {
  leadData.value.message = message;
  currentStep.value = 'thank_you';
};

// 跳过留言
const skipMessage = () => {
  currentStep.value = 'thank_you';
};

// 提交到 API
const submitToAPI = async (apiUrl: string): Promise<boolean> => {
  isSubmitting.value = true;
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(leadData.value),
    });

    if (response.ok) {
      pushToDataLayer('chat_lead_submitted', {
        service: leadData.value.service,
        contactMethod: leadData.value.contactMethod,
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to submit chat lead:', error);
    return false;
  } finally {
    isSubmitting.value = false;
  }
};

// 导出 composable
export function useChatWidget() {
  return {
    // 状态
    isOpen: computed(() => isOpen.value),
    currentStep: computed(() => currentStep.value),
    messages: computed(() => messages.value),
    leadData: computed(() => leadData.value),
    isSubmitting: computed(() => isSubmitting.value),
    
    // 方法
    openChat,
    closeChat,
    minimizeChat,
    resetChat,
    addMessage,
    selectService,
    selectContactMethod,
    submitContactValue,
    submitMessage,
    skipMessage,
    submitToAPI,
    
    // 直接暴露 ref 用于组件内部修改
    _currentStep: currentStep,
    _messages: messages,
    _leadData: leadData,
  };
}

